import { ArrowLeft, Heart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FavoriteStation } from '@evi/shared';
import { PhotoHero, StatusPill } from '@/components/primitives';
import { useFavorites } from '@/hooks/useFavorites';
import NamedIcon from '@/components/ui/NamedIcon';
import styles from './FavoritesPage.module.css';

interface Props { onNavigate: (path: string) => void }

/**
 * Driver's saved stations. Empty / loading / error states are all
 * first-class — if the user hasn't saved anything yet, we show a
 * friendly CTA back to the map instead of an empty card list.
 */
export function FavoritesPage({ onNavigate }: Props) {
  const { t } = useTranslation('favorites');
  const { data, isLoading, error } = useFavorites();
  const favorites = data?.favorites ?? [];

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backFab}
          aria-label={t('a11y.back')}
          onClick={() => onNavigate('/profile')}
        >
          <ArrowLeft size={16} />
        </button>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>{t('title')}</h1>
          <p className={styles.subtitle}>
            {isLoading
              ? t('loading')
              : t('subtitle', { count: favorites.length })}
          </p>
        </div>
      </header>

      {isLoading && <FavoritesLoading />}

      {!isLoading && error && (
        <div className={styles.errorBox}>
          {error instanceof Error ? error.message : t('loadError')}
        </div>
      )}

      {!isLoading && !error && favorites.length === 0 && (
        <FavoritesEmpty onExplore={() => onNavigate('/map')} />
      )}

      {!isLoading && !error && favorites.length > 0 && (
        <div className={styles.list}>
          {favorites.map((station) => (
            <FavoriteRow
              key={station.id}
              station={station}
              onOpen={() => onNavigate(`/stations/${station.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FavoriteRow({
  station, onOpen,
}: { station: FavoriteStation; onOpen: () => void }) {
  const { t } = useTranslation('favorites');
  const statusTone: 'good' | 'warn' =
    station.openStatus === 'closed' ? 'warn' : 'good';
  const statusLabel =
    station.openStatus === 'open_24_7'
      ? t('status.open24_7')
      : station.openStatus === 'open_now'
        ? t('status.open')
        : t('status.closed');

  return (
    <button type="button" className={styles.row} onClick={onOpen}>
      {station.photoUrl ? (
        <PhotoHero
          src={station.photoUrl}
          alt={station.name}
          height={120}
          topLeft={
            <StatusPill tone={statusTone} variant="solid" size="sm">
              {statusLabel}
            </StatusPill>
          }
        />
      ) : (
        // Photo-less fallback: a gradient strip with the status pill.
        // Keeps the row rhythm identical to photo-backed rows so the
        // list doesn't visually stutter when half of it has imagery.
        <div className={styles.photoFallback}>
          <StatusPill tone={statusTone} variant="solid" size="sm">
            {statusLabel}
          </StatusPill>
        </div>
      )}
      <div className={styles.rowBody}>
        {station.subtitle && (
          <p className={styles.rowEyebrow}>{station.subtitle}</p>
        )}
        <p className={styles.rowTitle}>{station.name}</p>
        <p className={styles.rowAddress}>{station.address}</p>
        <div className={styles.rowMeta}>
          <StatusPill tone="mute" variant="soft" size="sm">
            {t('stalls', {
              count: station.stallsTotal,
              available: station.stallsAvailable,
              total: station.stallsTotal,
            })}
          </StatusPill>
        </div>
      </div>
    </button>
  );
}

function FavoritesEmpty({ onExplore }: { onExplore: () => void }) {
  const { t } = useTranslation('favorites');
  return (
    <div className={styles.empty}>
      <div className={styles.emptyIcon} aria-hidden>
        <NamedIcon
          name="heart"
          fallback={<Heart size={40} fill="currentColor" />}
          size={40}
        />
      </div>
      <h2 className={styles.emptyTitle}>{t('empty.title')}</h2>
      <p className={styles.emptyDesc}>
        {t('empty.desc')}
      </p>
      <button type="button" className={styles.emptyCta} onClick={onExplore}>
        {t('empty.cta')}
      </button>
    </div>
  );
}

function FavoritesLoading() {
  return (
    <div className={styles.list} aria-busy aria-live="polite">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className={styles.rowSkeleton} />
      ))}
    </div>
  );
}
